import { useQuery } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"
import {
  createAgentInteraction,
  getAgentPerformance,
  getAgentPipeline,
  getAgentTasks,
} from "../api/endpoints/agents"
import { AgentClientDrawer } from "../components/agent_client_drawer"
import { PageSection, StatCard, StatusPill } from "../components/ui"
import { useAuth } from "../stores/auth"
import { formatCurrency, formatDateTime, formatEnumLabel } from "../utils/formatting"
import { flushInteractionQueue, loadAgentCache, saveAgentCache } from "../utils/offline_queue"

const PIPELINE_COLUMNS = ["NEW", "OLD", "BROKEN_PROMISE", "PROMISE_TO_PAY", "DONE"]

function pipelineTone(status: string): "neutral" | "success" | "warning" | "danger" | "info" {
  switch (status) {
    case "DONE":
      return "success"
    case "PROMISE_TO_PAY":
      return "info"
    case "BROKEN_PROMISE":
      return "danger"
    case "OLD":
      return "warning"
    default:
      return "neutral"
  }
}

function overdueTone(
  category?: string | null,
): "neutral" | "success" | "warning" | "danger" | "info" {
  switch (category) {
    case "CATEGORY_C":
    case "CATEGORY_Z":
      return "danger"
    case "CATEGORY_B":
      return "warning"
    case "CATEGORY_A":
    case "S1":
      return "info"
    default:
      return "neutral"
  }
}

function formatPhoneForWhatsApp(value?: string | null) {
  return value ? value.replace(/\D/g, "") : ""
}

export function AgentPipelinePage() {
  const { user } = useAuth()
  const agentId = user?.userId ?? ""
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null)
  const [search, setSearch] = useState("")
  const [overdueCategory, setOverdueCategory] = useState("")
  const [sortBy, setSortBy] = useState("days_overdue")
  const [sortOrder, setSortOrder] = useState("desc")
  const [page, setPage] = useState(1)
  const [activeColumn, setActiveColumn] = useState("NEW")

  const pipelineQuery = useQuery({
    queryKey: ["agent-pipeline", agentId, search, overdueCategory, sortBy, sortOrder, page],
    queryFn: () => {
      const params: Record<string, string> = {
        page: String(page),
        per_page: "50",
        sort_by: sortBy,
        sort_order: sortOrder,
      }

      if (search) params.search = search
      if (overdueCategory) params.overdue_category = overdueCategory

      return getAgentPipeline(agentId, params)
    },
    enabled: Boolean(agentId),
  })

  const tasksQuery = useQuery({
    queryKey: ["agent-tasks", agentId],
    queryFn: () => getAgentTasks(agentId),
    enabled: Boolean(agentId),
  })

  const performanceQuery = useQuery({
    queryKey: ["agent-performance", agentId],
    queryFn: () => getAgentPerformance(agentId),
    enabled: Boolean(agentId),
  })

  useEffect(() => {
    if (pipelineQuery.data?.data && agentId) {
      saveAgentCache(agentId, pipelineQuery.data.data)
    }
  }, [pipelineQuery.data, agentId])

  useEffect(() => {
    async function syncQueuedInteractions() {
      await flushInteractionQueue((queuedAgentId, payload) =>
        createAgentInteraction(queuedAgentId, payload),
      )
    }

    void syncQueuedInteractions()
    window.addEventListener("online", syncQueuedInteractions)
    return () => window.removeEventListener("online", syncQueuedInteractions)
  }, [])

  const cached = loadAgentCache(agentId)
  const rows = pipelineQuery.data?.data?.rows ?? cached.rows
  const summaryRows = pipelineQuery.data?.data?.summary ?? cached.summary
  const performance = performanceQuery.data?.data
  const tasks = tasksQuery.data?.data
  const meta = pipelineQuery.data?.meta

  const summaryMap = useMemo(() => {
    return Object.fromEntries(
      (summaryRows as any[]).map((entry) => [
        entry.pipelineStatus ?? entry.pipeline_status,
        entry,
      ]),
    )
  }, [summaryRows])

  const mobileRows = (rows as any[]).filter((row) => row.pipelineStatus === activeColumn)

  return (
    <div className="flex flex-col gap-5">
      <PageSection title="My pipeline">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard label="Assigned clients" value={performance?.assignedClients ?? 0} />
          <StatCard
            label="Outstanding amount"
            value={formatCurrency(performance?.outstandingAmount ?? 0)}
          />
          <StatCard
            label="Recovered amount"
            value={formatCurrency(performance?.recoveredAmount ?? 0)}
          />
          <StatCard
            helper={
              performance?.activeTarget?.targetPercentage
                ? `${Math.round(performance.activeTarget.targetPercentage * 100)}% target`
                : "No active target"
            }
            label="Interactions logged"
            value={performance?.interactionsLogged ?? 0}
          />
        </div>

        {!navigator.onLine ? (
          <div className="mt-5 rounded-r border-l-4 border-amber-500 bg-amber-50 p-3 text-sm text-amber-800">
            Offline mode is active. Cached pipeline data is shown and new interactions will sync
            later.
          </div>
        ) : null}

        <div className="mt-5 grid grid-cols-1 gap-3 lg:grid-cols-[2fr,1fr,1fr,auto]">
          <input
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(1)
            }}
            placeholder="Search client, phone, or loan ID"
            value={search}
          />
          <select
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            onChange={(event) => {
              setOverdueCategory(event.target.value)
              setPage(1)
            }}
            value={overdueCategory}
          >
            <option value="">All overdue categories</option>
            <option value="S1">S-1</option>
            <option value="CATEGORY_A">Category A</option>
            <option value="CATEGORY_B">Category B</option>
            <option value="CATEGORY_C">Category C</option>
          </select>
          <select
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            onChange={(event) => setSortBy(event.target.value)}
            value={sortBy}
          >
            <option value="days_overdue">Sort by days overdue</option>
            <option value="outstanding_balance">Sort by outstanding amount</option>
            <option value="last_contact_at">Sort by last interaction</option>
            <option value="ptp_date">Sort by PTP date</option>
            <option value="client_name">Sort by client name</option>
          </select>
          <button
            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700"
            onClick={() => setSortOrder((value) => (value === "asc" ? "desc" : "asc"))}
            type="button"
          >
            {sortOrder === "asc" ? "Ascending" : "Descending"}
          </button>
        </div>

        <div className="mt-5 flex gap-2 overflow-x-auto pb-1 lg:hidden">
          {PIPELINE_COLUMNS.map((column) => {
            const summary = summaryMap[column]
            return (
              <button
                className={`rounded-full border px-3 py-1.5 text-sm ${
                  activeColumn === column
                    ? "border-blue-600 bg-blue-50 text-blue-700"
                    : "border-slate-200 bg-white text-slate-600"
                }`}
                key={column}
                onClick={() => setActiveColumn(column)}
                type="button"
              >
                {formatEnumLabel(column)} ({summary?.clientCount ?? summary?.client_count ?? 0})
              </button>
            )
          })}
        </div>

        <div className="mt-5 flex flex-col gap-4 lg:hidden">
          {mobileRows.map((row: any) => {
            const whatsappNumber = formatPhoneForWhatsApp(row.phoneNumber)
            return (
              <button
                className="w-full rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm"
                key={row.loanId}
                onClick={() => setSelectedClientId(row.clientId)}
                type="button"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-slate-900">{row.clientName}</div>
                    <div className="text-sm text-slate-500">{row.loanId}</div>
                    <div className="text-sm text-slate-500">{row.productName}</div>
                  </div>
                  <StatusPill tone={pipelineTone(row.pipelineStatus)}>
                    {formatEnumLabel(row.pipelineStatus)}
                  </StatusPill>
                </div>
                <div className="mt-3 text-sm font-semibold text-slate-900">
                  {formatCurrency(row.outstandingBalance, row.currency)}
                </div>
                <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-600">
                  <StatusPill tone={overdueTone(row.overdueCategory)}>
                    {formatEnumLabel(row.overdueCategory)}
                  </StatusPill>
                  <span>{row.daysOverdue} days overdue</span>
                  <span>Last interaction: {formatDateTime(row.lastContactAt)}</span>
                  {row.ptpDate ? <span>PTP: {formatDateTime(row.ptpDate)}</span> : null}
                </div>
                <div className="mt-3 flex gap-2">
                  <a
                    className="rounded-md border border-blue-600 bg-blue-600 px-3 py-1 text-xs font-bold text-white"
                    href={`tel:${row.phoneNumber}`}
                    onClick={(event) => event.stopPropagation()}
                  >
                    Call
                  </a>
                  <a
                    className="rounded-md border border-slate-200 bg-white px-3 py-1 text-xs font-bold text-slate-700"
                    href={`sms:${row.phoneNumber}`}
                    onClick={(event) => event.stopPropagation()}
                  >
                    SMS
                  </a>
                  <a
                    className="rounded-md border border-emerald-600 bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700"
                    href={whatsappNumber ? `https://wa.me/${whatsappNumber}` : undefined}
                    onClick={(event) => event.stopPropagation()}
                    rel="noreferrer"
                    target="_blank"
                  >
                    WhatsApp
                  </a>
                </div>
              </button>
            )
          })}
        </div>

        <div className="mt-5 hidden grid-cols-1 gap-4 lg:grid lg:grid-cols-5">
          {PIPELINE_COLUMNS.map((column) => {
            const summary = summaryMap[column]
            const columnRows = (rows as any[]).filter((row) => row.pipelineStatus === column)
            return (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" key={column}>
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div>
                    <div className="font-semibold text-slate-900">{formatEnumLabel(column)}</div>
                    <div className="text-xs text-slate-500">
                      {summary?.clientCount ?? summary?.client_count ?? 0} clients ·{" "}
                      {formatCurrency(
                        summary?.outstandingAmount ?? summary?.outstanding_amount ?? 0,
                      )}
                    </div>
                  </div>
                  <StatusPill tone={pipelineTone(column)}>
                    {summary?.clientCount ?? summary?.client_count ?? 0}
                  </StatusPill>
                </div>
                <div className="flex flex-col gap-3">
                  {columnRows.map((row: any) => {
                    const whatsappNumber = formatPhoneForWhatsApp(row.phoneNumber)
                    return (
                      <button
                        className="w-full rounded-lg border border-slate-200 bg-white p-3 text-left shadow-sm transition-shadow hover:shadow"
                        key={row.loanId}
                        onClick={() => setSelectedClientId(row.clientId)}
                        type="button"
                      >
                        <div className="font-semibold text-slate-900">{row.clientName}</div>
                        <div className="text-xs text-slate-500">
                          {row.loanId} · {row.productName}
                        </div>
                        <div className="mt-2 text-sm font-semibold text-slate-900">
                          {formatCurrency(row.outstandingBalance, row.currency)}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-600">
                          <StatusPill tone={overdueTone(row.overdueCategory)}>
                            {formatEnumLabel(row.overdueCategory)}
                          </StatusPill>
                          <span>{row.daysOverdue} days overdue</span>
                          <span>Last: {formatDateTime(row.lastContactAt)}</span>
                          {row.ptpDate ? <span>PTP: {formatDateTime(row.ptpDate)}</span> : null}
                        </div>
                        <div className="mt-3 flex gap-2">
                          <a
                            className="rounded-md border border-blue-600 bg-blue-600 px-2 py-1 text-[11px] font-bold text-white"
                            href={`tel:${row.phoneNumber}`}
                            onClick={(event) => event.stopPropagation()}
                          >
                            Call
                          </a>
                          <a
                            className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-bold text-slate-700"
                            href={`sms:${row.phoneNumber}`}
                            onClick={(event) => event.stopPropagation()}
                          >
                            SMS
                          </a>
                          <a
                            className="rounded-md border border-emerald-600 bg-emerald-50 px-2 py-1 text-[11px] font-bold text-emerald-700"
                            href={whatsappNumber ? `https://wa.me/${whatsappNumber}` : undefined}
                            onClick={(event) => event.stopPropagation()}
                            rel="noreferrer"
                            target="_blank"
                          >
                            WA
                          </a>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>

        <div className="mt-5 flex items-center justify-between text-sm text-slate-600">
          <span>
            Page {meta?.page ?? 1} of {meta?.totalPages ?? 1}
          </span>
          <div className="flex gap-2">
            <button
              className="rounded-md border border-slate-200 bg-white px-3 py-1.5"
              disabled={(meta?.page ?? 1) <= 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
              type="button"
            >
              Previous
            </button>
            <button
              className="rounded-md border border-slate-200 bg-white px-3 py-1.5"
              disabled={(meta?.page ?? 1) >= (meta?.totalPages ?? 1)}
              onClick={() => setPage((value) => value + 1)}
              type="button"
            >
              Next
            </button>
          </div>
        </div>
      </PageSection>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <PageSection title="Follow-ups due">
          <ul className="flex flex-col gap-3 list-none p-0 m-0">
            {(tasks?.followUps ?? []).map((entry: any) => (
              <li
                className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3"
                key={`${entry.clientId}-${entry.followUpDate}`}
              >
                <div>
                  <strong>{entry.clientName}</strong>
                  <div className="text-xs text-slate-500">{entry.loanId}</div>
                  <div className="text-sm text-slate-600">{formatDateTime(entry.followUpDate)}</div>
                </div>
                <button
                  className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700"
                  onClick={() => setSelectedClientId(entry.clientId)}
                  type="button"
                >
                  Open
                </button>
              </li>
            ))}
          </ul>
        </PageSection>
        <PageSection title="Expiring promises">
          <ul className="flex flex-col gap-3 list-none p-0 m-0">
            {(tasks?.expiringPromises ?? []).map((entry: any) => (
              <li
                className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3"
                key={`${entry.clientId}-${entry.ptpDate}`}
              >
                <div>
                  <strong>{entry.clientName}</strong>
                  <div className="text-xs text-slate-500">{entry.loanId}</div>
                  <div className="text-sm text-slate-600">{formatDateTime(entry.ptpDate)}</div>
                </div>
                <button
                  className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700"
                  onClick={() => setSelectedClientId(entry.clientId)}
                  type="button"
                >
                  Open
                </button>
              </li>
            ))}
          </ul>
        </PageSection>
      </div>

      <AgentClientDrawer
        agentId={agentId}
        clientId={selectedClientId}
        onClose={() => setSelectedClientId(null)}
      />
    </div>
  )
}
